/**
 * Training run truth (T9, Pavel 2026-08-05): ONE module for what a training
 * run is, how the runner reports it, and how every surface derives its state.
 *
 * The Instance Creator on the Mac Mini builds the client's Buddy in 9 phases.
 * It reports progress to POST /api/automation/clients/:id/training-progress
 * (contract in docs/training-progress-automation.md); the partner console
 * consumes the stored run through the staff detail. Nobody re-derives run
 * state on a surface - extend this module instead (fixr4 law).
 */

export type TrainingRunStatus = "running" | "waiting_input" | "done" | "failed" | "stopped";

export type TrainingPhaseStatus = "running" | "done" | "failed";

export interface TrainingPhaseDto {
  /** 1-based phase number from the Instance Creator workflow. */
  phase: number;
  status: TrainingPhaseStatus;
  startedAt: string;
  /** Present once the phase finished (done or failed). */
  finishedAt?: string | null;
  durationS?: number | null;
  /** Runner's short progress message for the phase ("Nahravam 42 zdroju..."). */
  detail?: string | null;
  error?: string | null;
}

export interface TrainingEventDto {
  ts: string;
  kind: "info" | "milestone" | "error";
  text: string;
  phase?: number | null;
}

export interface TrainingRunDto {
  runId: string;
  status: TrainingRunStatus;
  phases: TrainingPhaseDto[];
  events: TrainingEventDto[];
  currentPhase: number | null;
  botUsername: string | null;
  note: string | null;
  waiting: { type: string; message: string } | null;
  startedAt: string;
  updatedAt: string;
  finishedAt: string | null;
}

/**
 * The 9-phase catalog of the Instance Creator workflow (mirror of
 * DEFAULT_PHASES in the hub dashboard; the runner reports phase NUMBERS, the
 * console renders localized names via `partnerOS.trainingRun.phase.N`).
 * `typicalS` = average durations measured over 20 real runs (hub
 * PHASE_AVG_SECONDS, 2026-06). They are shown as "obvykle ~X" hints, never as
 * promises; phase 8 dominates the wall clock (fine-tuning ~40 min).
 */
export const TRAINING_PHASES: ReadonlyArray<{ phase: number; typicalS: number }> = [
  { phase: 1, typicalS: 72 },
  { phase: 2, typicalS: 232 },
  { phase: 3, typicalS: 53 },
  { phase: 4, typicalS: 9 },
  { phase: 5, typicalS: 4 },
  { phase: 6, typicalS: 73 },
  { phase: 7, typicalS: 82 },
  { phase: 8, typicalS: 2415 },
  { phase: 9, typicalS: 168 }
];

export const TRAINING_PHASE_COUNT = TRAINING_PHASES.length;

export function trainingPhaseLabelKey(phase: number): string | null {
  return phase >= 1 && phase <= TRAINING_PHASE_COUNT
    ? `partnerOS.trainingRun.phase.${phase}`
    : null;
}

export function trainingPhaseTypicalS(phase: number): number | null {
  return TRAINING_PHASES.find((entry) => entry.phase === phase)?.typicalS ?? null;
}

/**
 * A running run that has not reported for this long is SILENT: the runner
 * heartbeats at least once a minute while alive (contract), so ten quiet
 * minutes mean the process died, hangs, or lost network - the partner must
 * see that instead of a green "working" chip. Same constant family as
 * DFY_RUN_SILENT_MS; phase length does not matter because heartbeats are
 * time-based, not phase-based.
 */
export const TRAINING_RUN_SILENT_MS = 10 * 60 * 1000;

export function isTrainingRunStale(
  run: Pick<TrainingRunDto, "status" | "updatedAt">,
  now: number
): boolean {
  if (run.status !== "running" && run.status !== "waiting_input") return false;
  const updated = Date.parse(run.updatedAt);
  if (Number.isNaN(updated)) return true;
  return now - updated > TRAINING_RUN_SILENT_MS;
}

/**
 * What the training workspace SHOWS. Derived from the canonical section
 * state, the stored run and the trial link - one place, consumed by the card
 * and by tests.
 *
 *  - locked:   training has not started (rail lock speaks, not this card)
 *  - queued:   training working, no run reported yet (honest cold start;
 *              the only place the static "2-3 dny" survives)
 *  - running:  live run, fresh heartbeat
 *  - waiting:  runner asked for human input (voice clone URL)
 *  - stale:    live run, silent > 10 min
 *  - failed:   the run reported failure (resume offered)
 *  - stopped:  the run was stopped by an operator on the Mini
 *  - done:     result landed (trial link) or run reported done
 */
export type TrainingView =
  | { state: "locked" | "queued"; run: null }
  | { state: "running" | "waiting" | "stale" | "failed" | "stopped" | "done"; run: TrainingRunDto };

export function trainingRunView(input: {
  sectionState: "locked" | "next" | "working" | "done" | null;
  run: TrainingRunDto | null;
  hasTrialLink: boolean;
  now: number;
}): TrainingView {
  const { run } = input;
  // The result is the truth of DONE: even a run that never reported its final
  // POST cannot hide a delivered bot (trial link wins), and a finished step
  // with no run history still recaps as done.
  if (input.hasTrialLink || input.sectionState === "done") {
    return run ? { state: "done", run } : { state: "locked", run: null };
  }
  if (!run) {
    return input.sectionState === "working"
      ? { state: "queued", run: null }
      : { state: "locked", run: null };
  }
  if (run.status === "done") return { state: "done", run };
  if (run.status === "failed") return { state: "failed", run };
  if (run.status === "stopped") return { state: "stopped", run };
  if (isTrainingRunStale(run, input.now)) return { state: "stale", run };
  if (run.status === "waiting_input") return { state: "waiting", run };
  return { state: "running", run };
}

/** May the partner ask the Mini to pick the run up again? Server enforces the
 * same predicate; the button never offers what the server refuses. */
export function trainingResumeAllowed(view: TrainingView): boolean {
  return view.state === "failed" || view.state === "stale" || view.state === "stopped";
}

/**
 * A fired resume is a state of its own (admin-ops pattern): the request is
 * confirmed by the audit row, the RESULT only by the runner reporting again.
 * "requested" while the request is newer than the run's last report and
 * younger than the silence window; after that it degrades to "silent" so a
 * runner that never answered stays visible instead of looking handled.
 */
export function trainingResumeState(
  run: TrainingRunDto | null,
  resumeRequestedAt: string | null,
  now: number
): "requested" | "silent" | null {
  if (!resumeRequestedAt) return null;
  const requested = Date.parse(resumeRequestedAt);
  if (Number.isNaN(requested)) return null;
  if (run && Date.parse(run.updatedAt) >= requested) return null;
  return now - requested > TRAINING_RUN_SILENT_MS ? "silent" : "requested";
}

// ---- Machine payload validation (worker side; also unit-tested) ----

const RUN_STATUSES: ReadonlySet<string> = new Set(["running", "waiting_input", "done", "failed", "stopped"]);
const PHASE_STATUSES: ReadonlySet<string> = new Set(["running", "done", "failed"]);
const EVENT_KINDS: ReadonlySet<string> = new Set(["info", "milestone", "error"]);
export const TRAINING_EVENTS_CAP = 40;
const TEXT_CAP = 500;

export interface TrainingProgressPayload {
  runId: string;
  status: TrainingRunStatus;
  startedAt: string;
  phases: TrainingPhaseDto[];
  events?: TrainingEventDto[];
  currentPhase?: number | null;
  botUsername?: string | null;
  note?: string | null;
  waiting?: { type: string; message: string } | null;
}

function validIso(value: unknown): value is string {
  return typeof value === "string" && value.length <= 40 && !Number.isNaN(Date.parse(value));
}

function optionalText(value: unknown, cap = TEXT_CAP): string | null {
  return typeof value === "string" && value.trim() ? value.trim().slice(0, cap) : null;
}

/** Parse + normalize the runner payload; null = 400. Unknown extra fields are
 * ignored so the Mini adapter can grow without breaking old workers. */
export function parseTrainingProgressPayload(body: unknown): TrainingProgressPayload | null {
  if (!body || typeof body !== "object" || Array.isArray(body)) return null;
  const raw = body as Record<string, unknown>;
  const runId = typeof raw.runId === "string" ? raw.runId.trim() : "";
  if (!runId || runId.length > 80 || !/^[A-Za-z0-9._:-]+$/.test(runId)) return null;
  if (typeof raw.status !== "string" || !RUN_STATUSES.has(raw.status)) return null;
  if (!validIso(raw.startedAt)) return null;
  if (!Array.isArray(raw.phases) || raw.phases.length > 20) return null;
  const phases: TrainingPhaseDto[] = [];
  for (const entry of raw.phases) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return null;
    const item = entry as Record<string, unknown>;
    const phase = Number(item.phase);
    if (!Number.isInteger(phase) || phase < 1 || phase > 20) return null;
    if (typeof item.status !== "string" || !PHASE_STATUSES.has(item.status)) return null;
    if (!validIso(item.startedAt)) return null;
    if (item.finishedAt !== undefined && item.finishedAt !== null && !validIso(item.finishedAt)) return null;
    const durationS = item.durationS === undefined || item.durationS === null ? null : Number(item.durationS);
    if (durationS !== null && (!Number.isFinite(durationS) || durationS < 0)) return null;
    phases.push({
      phase,
      status: item.status as TrainingPhaseStatus,
      startedAt: item.startedAt as string,
      finishedAt: validIso(item.finishedAt) ? item.finishedAt : null,
      durationS,
      detail: optionalText(item.detail),
      error: optionalText(item.error, 1000)
    });
  }
  let events: TrainingEventDto[] | undefined;
  if (raw.events !== undefined) {
    if (!Array.isArray(raw.events) || raw.events.length > 200) return null;
    events = [];
    for (const entry of raw.events) {
      if (!entry || typeof entry !== "object" || Array.isArray(entry)) return null;
      const item = entry as Record<string, unknown>;
      if (!validIso(item.ts)) return null;
      if (typeof item.kind !== "string" || !EVENT_KINDS.has(item.kind)) return null;
      const text = optionalText(item.text);
      if (!text) return null;
      const phase = item.phase === undefined || item.phase === null ? null : Number(item.phase);
      if (phase !== null && !Number.isInteger(phase)) return null;
      events.push({ ts: item.ts as string, kind: item.kind as TrainingEventDto["kind"], text, phase });
    }
    // The feed is a short curated tail, never a log dump.
    events = events.slice(-TRAINING_EVENTS_CAP);
  }
  const currentPhase = raw.currentPhase === undefined || raw.currentPhase === null
    ? null
    : Number(raw.currentPhase);
  if (currentPhase !== null && (!Number.isInteger(currentPhase) || currentPhase < 0 || currentPhase > 20)) return null;
  let waiting: { type: string; message: string } | null = null;
  if (raw.waiting !== undefined && raw.waiting !== null) {
    if (typeof raw.waiting !== "object" || Array.isArray(raw.waiting)) return null;
    const item = raw.waiting as Record<string, unknown>;
    const type = optionalText(item.type, 60);
    const message = optionalText(item.message);
    if (!type || !message) return null;
    waiting = { type, message };
  }
  const botUsername = optionalText(raw.botUsername, 64);
  return {
    runId,
    status: raw.status as TrainingRunStatus,
    startedAt: raw.startedAt as string,
    phases,
    events,
    currentPhase,
    botUsername: botUsername ? botUsername.replace(/^@/, "") : null,
    note: optionalText(raw.note, 2000),
    waiting
  };
}

export function parseTrainingPhases(json: unknown): TrainingPhaseDto[] {
  if (typeof json !== "string" || !json) return [];
  try {
    const parsed = JSON.parse(json) as unknown;
    return Array.isArray(parsed) ? parsed as TrainingPhaseDto[] : [];
  } catch { return []; }
}

export function parseTrainingEvents(json: unknown): TrainingEventDto[] {
  if (typeof json !== "string" || !json) return [];
  try {
    const parsed = JSON.parse(json) as unknown;
    return Array.isArray(parsed) ? parsed as TrainingEventDto[] : [];
  } catch { return []; }
}
